import { useEffect, useState } from 'react';
import {
  EquipmentType,
  ObservableLevelsEnum,
  TaskStatus,
} from '../../data/enums';
import { inspectionDB } from '../../data/database.structure';
import { ProgressIndicatorService } from '../../services/progress-indicator.service';
import progressIndicators from '../../data/local-files/progress-indicators';
import { useInspection } from '../useInspection';
import InspectionUndercarriageSideScreenService from './inspectionUndercarriageSideScreen.service';
import { use } from 'marked';
import useInspectionBucketWearKit from './useInspectionBucketWearKit';
import useBucketGETPartTypes from '../useBucketGETPartTypes';
import useInspectionBucketGET from './useInspectionBucketGET';
import { useCurrentInspectionAssetEquipmentCached } from '../useCurrentInspectionAssetEquipmentCached';
import { set } from 'date-fns';
import { get } from 'react-hook-form';

const useInspectionTableOfContentsIndicators = (inspectionId: string) => {
  const { inspection } = useInspection(inspectionId);

  const [inspectionDateIndicator, setInspectionDateIndicator] =
    useState<TaskStatus>(TaskStatus.notStarted);
  const [machinePhotoIndicator, setMachinePhotoIndicator] =
    useState<TaskStatus>(TaskStatus.notStarted);
  const [operationIndicator, setOperationIndicator] = useState<TaskStatus>(
    TaskStatus.notStarted,
  );
  const [inspectionSummaryIndicator, setInspectionSummaryIndicator] =
    useState<TaskStatus>(TaskStatus.notStarted);
  const [undercarriageIndicator, setUndercarriageIndicator] = useState<
    TaskStatus | undefined
  >(undefined);
  const [bucketIndicator, setBucketIndicator] = useState<TaskStatus>(
    TaskStatus.notStarted,
  );

  const { bucketGETPartTypes } = useBucketGETPartTypes();
  const inspectionBucketState = useCurrentInspectionAssetEquipmentCached({
    inspectionId: inspectionId,
  });

  // Bucket Indicators
  const {
    wearPlatesIndicator,
    heelShroudsIndicator,
    isReady: isReadyBucketWearKit,
    summary: wearKitSummary,
  } = useInspectionBucketWearKit(inspectionId);

  const {
    isReady: isReadyBucketGET,
    partTypesIndicators,
    summary: getSummary,
  } = useInspectionBucketGET({
    inspectionId,
    partTypes: bucketGETPartTypes,
    bucket: inspectionBucketState.bucket ?? undefined,
  });

  useEffect(() => {
    if (!inspection) {
      setInspectionDateIndicator(TaskStatus.notStarted);
      setMachinePhotoIndicator(TaskStatus.notStarted);
      setOperationIndicator(TaskStatus.notStarted);
      setInspectionSummaryIndicator(TaskStatus.notStarted);
      return;
    }

    const service = new ProgressIndicatorService(
      inspectionDB,
      inspection.inspection,
    );

    const externalId =
      inspection.asset?.externalId ||
      inspection.mineLocation?.mineLocation.externalId ||
      '';
    const level = inspection.asset
      ? ObservableLevelsEnum.asset
      : ObservableLevelsEnum.mineLocation;

    // Inspection Date Screen
    const inspectionDateScreen = progressIndicators.Root.InspectionDate;
    const inspectionDateIndicator = service.processScreen({
      screen: inspectionDateScreen,
      assetExternalId: inspection.asset?.externalId,
      dataLevel: level,
      entityExternalId: externalId,
    });
    setInspectionDateIndicator(inspectionDateIndicator);

    // Machine Photo Screen
    const machinePhoto = progressIndicators.Root.MachinePhoto;
    const machinePhotoIndicator = service.processScreen({
      screen: machinePhoto,
      assetExternalId: inspection.asset?.externalId,
      dataLevel: ObservableLevelsEnum.asset,
      entityExternalId: inspection.asset?.externalId || '',
    });
    setMachinePhotoIndicator(machinePhotoIndicator);

    // Operation Screen
    const operation = progressIndicators.Root.Operation;
    const operationIndicator = service.processScreen({
      screen: operation,
      assetExternalId: inspection.asset?.externalId,
      dataLevel: ObservableLevelsEnum.asset,
      entityExternalId: inspection.asset?.externalId || '',
    });
    setOperationIndicator(operationIndicator);

    // Inspection Summary Screen
    const inspectionSummary = progressIndicators.Root.ExecutiveSummary;
    const inspectionSummaryIndicator = service.processScreen({
      screen: inspectionSummary,
      assetExternalId: inspection.asset?.externalId,
      dataLevel: inspection.asset
        ? ObservableLevelsEnum.asset
        : ObservableLevelsEnum.mineLocation,
      entityExternalId:
        inspection.asset?.externalId ||
        inspection.mineLocation?.mineLocation.externalId ||
        '',
    });
    setInspectionSummaryIndicator(inspectionSummaryIndicator);

    // Undercarriage Side Indicators
    if (inspection.asset && inspection.asset.equipments.length > 0) {
      const statuss: TaskStatus[] = [];

      inspection.asset.equipments.forEach((equipment) => {
        if (equipment.equipmentCategory === EquipmentType.undercarriage) {
          const sideIndicator = new InspectionUndercarriageSideScreenService(
            inspection,
            equipment,
          ).getSideIndicators();
          const sideStatus = Object.values(sideIndicator).filter(
            (status) => status !== undefined,
          );
          statuss.push(...sideStatus);
        }
      });

      if (statuss.length === 0) {
        setUndercarriageIndicator(undefined);
      } else {
        const baseNotStarted = statuss.every(
          (status) => status === TaskStatus.notStarted,
        );
        if (baseNotStarted) {
          setUndercarriageIndicator(TaskStatus.notStarted);
        } else if (statuss.every((status) => status === TaskStatus.completed)) {
          setUndercarriageIndicator(TaskStatus.completed);
        } else {
          setUndercarriageIndicator(TaskStatus.inProgress);
        }
      }
    } else {
      setUndercarriageIndicator(undefined);
    }

    // Bucket Indicator

    const bucketSummaryStatus = inspectionBucketState.bucket
      ? service.processScreen({
          screen: progressIndicators.Root.Bucket.children.Summary,
          entityExternalId: inspectionBucketState.bucket.externalId,
          dataLevel: ObservableLevelsEnum.equipment,
        })
      : TaskStatus.notStarted;

    const bucketStatuses: TaskStatus[] = [
      wearKitSummary,
      ...partTypesIndicators?.map((indicator) => indicator.status),
      ...(heelShroudsIndicator ? [heelShroudsIndicator] : []),
      ...(wearPlatesIndicator ? [wearPlatesIndicator] : []),
      ...(getSummary ? [getSummary] : []),
      bucketSummaryStatus,
    ];

    var bucketStatus = TaskStatus.notStarted;

    if (bucketStatuses.every((status) => status === TaskStatus.completed)) {
      bucketStatus = TaskStatus.completed;
    } else if (
      bucketStatuses.some(
        (status) =>
          status === TaskStatus.inProgress || status === TaskStatus.completed,
      )
    ) {
      bucketStatus = TaskStatus.inProgress;
    } else {
      bucketStatus = TaskStatus.notStarted;
    }

    setBucketIndicator(bucketStatus);
  }, [
    inspection,
    wearPlatesIndicator,
    heelShroudsIndicator,
    partTypesIndicators,
    getSummary,
    wearKitSummary,
    inspectionBucketState.bucket,
  ]);

  return {
    inspectionDateIndicator,
    machinePhotoIndicator,
    operationIndicator,
    inspectionSummaryIndicator,
    undercarriageIndicator,
    bucketIndicator,
    // Whether the inspection actually has a bucket - consumers counting
    // sections (the InspectionCard progress bar) must exclude the bucket
    // indicator when there is none, or UC-only machines can never reach 100%.
    hasBucket: !!inspectionBucketState.bucket,
  };
};

export default useInspectionTableOfContentsIndicators;
